/**
 * MotionWrapper Component
 *
 * Reusable wrapper for Framer Motion animations with common presets.
 * Reduces code duplication and ensures consistent animations across the app.
 */

import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import type { ReactNode } from "react";
import {
  INITIAL_STATES,
  ANIMATE_STATES,
  TRANSITIONS,
  ANIMATION_DELAYS,
} from "../constants/animations";

type AnimationPreset =
  | "fadeIn"
  | "fadeUp"
  | "fadeLeft"
  | "fadeDown"
  | "scale"
  | "none";

interface MotionWrapperProps
  extends Omit<HTMLMotionProps<"div">, "initial" | "animate" | "transition"> {
  children: ReactNode;
  /** Animation preset to use */
  preset?: AnimationPreset;
  /** Skip animation entirely - useful for navigation */
  skipAnimation?: boolean;
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Enable layout animations for smooth repositioning */
  layout?: boolean;
  /** Callback when animation completes */
  onComplete?: () => void;
  /** Custom className */
  className?: string;
}

/**
 * Get initial and animate states for a given preset
 */
function getAnimationStates(preset: AnimationPreset) {
  switch (preset) {
    case "fadeIn":
      return {
        initial: INITIAL_STATES.hidden,
        animate: ANIMATE_STATES.visible,
      };
    case "fadeUp":
      return {
        initial: INITIAL_STATES.fadeUp,
        animate: ANIMATE_STATES.visibleCenter,
      };
    case "fadeLeft":
      return {
        initial: INITIAL_STATES.fadeLeft,
        animate: ANIMATE_STATES.visibleCenter,
      };
    case "fadeDown":
      return {
        initial: INITIAL_STATES.fadeDown,
        animate: ANIMATE_STATES.visibleCenter,
      };
    case "scale":
      return {
        initial: INITIAL_STATES.scale,
        animate: ANIMATE_STATES.visibleScale,
      };
    case "none":
    default:
      return {
        initial: ANIMATE_STATES.visible,
        animate: ANIMATE_STATES.visible,
      };
  }
}

/**
 * MotionWrapper - Reusable animated container
 *
 * @example
 * // Simple fade in
 * <MotionWrapper preset="fadeIn">
 *   <div>Content</div>
 * </MotionWrapper>
 *
 * @example
 * // Fade up with delay and callback
 * <MotionWrapper
 *   preset="fadeUp"
 *   delay={0.3}
 *   onComplete={() => console.log("Done!")}
 * >
 *   <div>Content</div>
 * </MotionWrapper>
 *
 * @example
 * // Skip animation when navigating backward
 * <MotionWrapper preset="fadeUp" skipAnimation={isNavigatingBack}>
 *   <div>Content</div>
 * </MotionWrapper>
 */
export function MotionWrapper({
  children,
  preset = "fadeIn",
  skipAnimation = false,
  delay = ANIMATION_DELAYS.none,
  layout = false,
  onComplete,
  className,
  ...motionProps
}: MotionWrapperProps) {
  const states = getAnimationStates(preset);

  // When skipping animation, show immediately
  const initial = skipAnimation ? states.animate : states.initial;
  const animate = states.animate;

  // Transition with delay
  const transition = {
    ...TRANSITIONS.fade,
    delay: skipAnimation ? 0 : delay,
  };

  return (
    <motion.div
      layout={layout}
      initial={initial}
      animate={animate}
      transition={transition}
      onAnimationComplete={onComplete}
      className={className}
      {...motionProps}
    >
      {children}
    </motion.div>
  );
}

/**
 * ButtonMotionWrapper - Specialized wrapper for buttons with spring animation
 *
 * @example
 * <ButtonMotionWrapper delay={0.3} onComplete={onButtonReady}>
 *   <button>Click me</button>
 * </ButtonMotionWrapper>
 */
export function ButtonMotionWrapper({
  children,
  skipAnimation = false,
  delay = ANIMATION_DELAYS.normal,
  onComplete,
  className,
  ...motionProps
}: Omit<MotionWrapperProps, "preset" | "layout">) {
  const initial = skipAnimation
    ? ANIMATE_STATES.visibleScale
    : INITIAL_STATES.scale;

  // `MotionConfig reducedMotion="user"` only makes transform transitions
  // instant — it doesn't drop the target value, so the hover/tap scale still
  // snaps in. Gate the gesture targets on the hook so reduced-motion users get
  // no scale at all (the CSS color fade remains as interactive feedback).
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      layout
      initial={initial}
      animate={ANIMATE_STATES.visibleScale}
      transition={{
        ...TRANSITIONS.button,
        delay: skipAnimation ? 0 : delay,
      }}
      whileHover={prefersReducedMotion ? undefined : { scale: 1.05 }}
      whileTap={prefersReducedMotion ? undefined : { scale: 0.95 }}
      onAnimationComplete={onComplete}
      className={className}
      {...motionProps}
    >
      {children}
    </motion.div>
  );
}
